import { createSlice, WritableDraft } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ICartItem } from '../types';
import { IDiscount } from '../../_types';

interface BrandCart {
  isCheckout: boolean;
  products: ICartItem[];
}

interface CartState {
  cart: {
    [brand: string]: BrandCart;
  };
}

const initialState: CartState = {
  cart: {},
};

// Ensure brand exists in state
function ensureBrand(state: WritableDraft<CartState>, shipper_id: string) {
  if (!state?.cart?.[shipper_id]) {
    if (state?.cart?.cart || Array.isArray(state?.cart)) {
      state.cart = { [shipper_id]: { isCheckout: false, products: [] } };
    } else {
      state.cart[shipper_id] = { isCheckout: false, products: [] };
    }
  }
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // ✅ ADD_TO_CART
    ADD_TO_CART: (
      state,
      action: {
        payload: ICartItem & { key?: string; brand?: string };
      }
    ) => {
      const shipperId = action?.payload?.shipper_id;
      ensureBrand(state, shipperId);

      if (Array.isArray(state.cart)) {
        state.cart = {};
      }

      const products = state.cart?.[shipperId]?.products || [];

      if (
        action.payload.type === 'simple' &&
        action.payload.order_modifiers.length === 0
      ) {
        const itemInCart = products.find(
          (item) => item.product_id === action.payload.product_id
        );

        if (itemInCart) {
          if (!itemInCart.default_product) itemInCart.quantity++;
        } else {
          products.push({
            ...action.payload,
            quantity: action.payload.quantity || 1,
            key: action.payload.key || uuidv4(),
          });
        }
      } else {
        products.push({
          ...action.payload,
          quantity: action.payload.quantity || 1,
          key: action.payload.key || uuidv4(),
        });
      }
    },

    // ✅ UPDATE_CART
    UPDATE_CART: (
      state,
      action: { payload: { shipper_id: string; products: ICartItem[] } }
    ) => {
      const { shipper_id, products } = action.payload;

      if (Array.isArray(state.cart)) {
        state.cart = {};
      }

      if (!state.cart[shipper_id]) {
        state.cart[shipper_id] = { isCheckout: false, products: [] };
      }
      state.cart[shipper_id].products = products;
    },

    // ✅ SET_QUANTITY
    SET_QUANTITY: (
      state,
      action: { payload: { shipper_id: string; key: string; quantity: number } }
    ) => {
      const { shipper_id, key, quantity } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.key === key ? { ...item, quantity } : item
      );
    },

    // ✅ PATCH_CART_ITEM — partial update (auto-add min/max/qty sync)
    PATCH_CART_ITEM: (
      state,
      action: {
        payload: { shipper_id: string; key: string } & Partial<ICartItem>;
      }
    ) => {
      const { shipper_id, key, ...patch } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map(
        (item) => (item.key === key ? { ...item, ...patch, key: item.key } : item)
      );
    },

    // ✅ INCREMENT
    INCREMENT: (state, action: { payload: { shipper_id: string; key: string } }) => {
      const { shipper_id, key } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.key === key ? { ...item, quantity: item.quantity + 1 } : item
      );
    },

    // ✅ DECREMENT
    DECREMENT: (state, action: { payload: { shipper_id: string; key: string } }) => {
      const { shipper_id, key } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.key === key
          ? { ...item, quantity: item.quantity === 1 ? 1 : item.quantity - 1 }
          : item
      );
    },

    // ✅ REMOVE
    REMOVE: (state, action: { payload: { shipper_id: string; key: string } }) => {
      const { shipper_id, key } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.filter(
        (item) => item.key !== key
      );
    },

    // ✅ REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION — remove items whose category_is_restricted !== is_restricted (used when adding a product from a restricted/non-restricted category)
    REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION: (
      state,
      action: { payload: { shipper_id: string; is_restricted: boolean } }
    ) => {
      const { shipper_id, is_restricted } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.filter(
        (item) => (item.category_is_restricted ?? false) === is_restricted
      );
    },

    // ✅ UPDATE_MODIFIERS
    UPDATE_MODIFIERS: (
      state,
      action: {
        payload: {
          shipper_id: string;
          key: string;
          data: ICartItem['order_modifiers'];
        };
      }
    ) => {
      const { shipper_id, key, data } = action.payload;
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.key === key ? { ...item, order_modifiers: data } : item
      );
    },

    // ✅ STOP (in_stop = true)
    STOP: (
      state,
      action: { payload: { shipper_id: string; product_id: string } }
    ) => {
      const { shipper_id, product_id } = action.payload;

      if (Array.isArray(state.cart)) {
        state.cart = {};
      }

      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.product_id === product_id ? { ...item, in_stop: true } : item
      );
    },

    // ✅ EXCLUDE (in_stop = false)
    EXCLUDE: (
      state,
      action: { payload: { shipper_id: string; product_id: string } }
    ) => {
      const { shipper_id, product_id } = action.payload;

      if (Array.isArray(state.cart)) {
        state.cart = {};
      }
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.product_id === product_id && item.in_stop
          ? { ...item, in_stop: false }
          : item
      );
    },

    // ✅ EXCLUDE_STOP (barcha in_stop ni false qilish)
    EXCLUDE_STOP: (state, action: { payload: { shipper_id: string } }) => {
      const { shipper_id } = action.payload;
      if (Array.isArray(state.cart)) {
        state.cart = {};
      }
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) =>
        item.in_stop ? { ...item, in_stop: false } : item
      );
    },

    // ✅ UPDATE_PRICES (discountlar bilan)
    UPDATE_PRICES: (
      state,
      action: { payload: { shipper_id: string; discounts: IDiscount[] } }
    ) => {
      const { shipper_id, discounts } = action.payload;
      if (Array.isArray(state.cart)) {
        state.cart = {};
      }
      if (!state.cart[shipper_id]) return;
      state.cart[shipper_id].products = state.cart[shipper_id].products.map((item) => {
        const totalDiscount = discounts.reduce((acc, cur) => {
          if (
            cur?.product_and_discount &&
            Object.keys(cur.product_and_discount).length > 0
          ) {
            const d = cur.product_and_discount[item.product_id];
            if (d) acc += d;
          } else if (cur.discount_for === 'product') {
            if (cur.discount_mode === 'fixed') {
              acc += cur.discount_amount;
            } else if (cur.discount_mode === 'percent') {
              if (item.price > 0)
                acc += (item.price / 100) * cur.discount_amount;
            }
          }
          return acc;
        }, 0);

        const price_with_discount = Math.max(0, item.price - totalDiscount);
        const discount_percent =
          item.price > 0 && totalDiscount > 0
            ? Math.floor((totalDiscount / item.price) * 100)
            : 0;

        return {
          ...item,
          price_with_discount,
          discount_percent,
        };
      });
    },

    CLEAR: (state, action: { payload: { shipper_id: string } }) => {
      const { shipper_id } = action.payload || {};

      if (Array.isArray(state.cart)) {
        state.cart = {};
      }

      if (shipper_id) {
        if (state.cart[shipper_id]) {
          state.cart[shipper_id].products = [];
          state.cart[shipper_id].isCheckout = false;
        }
        // } else {
        //   Object.keys(state.cart).forEach((b) => {
        //     state.cart[b].products = [];
        //     state.cart[b].isCheckout = false;
        //   });
      }
    },

    // ✅ ADD_COMMENT
    ADD_COMMENT: (
      state,
      action: { payload: { shipper_id: string; key: string; comment: string } }
    ) => {
      const { shipper_id, key, comment } = action.payload;
      const item = state.cart[shipper_id].products.find((item) => item.key === key);
      if (item) item.comment = comment;
    },

    // ✅ DELETE_COMMENT
    DELETE_COMMENT: (
      state,
      action: { payload: { shipper_id: string; key: string } }
    ) => {
      const { shipper_id, key } = action.payload;
      const item = state.cart[shipper_id].products.find((item) => item.key === key);
      if (item) item.comment = '';
    },

    // ✅ TOGGLE_CHECKOUT
    TOGGLE_CHECKOUT: (state, action: { payload: { shipper_id: string } }) => {
      const { shipper_id } = action.payload;
      if (!state.cart[shipper_id]) {
        state.cart[shipper_id] = { isCheckout: false, products: [] };
      }
      state.cart[shipper_id].isCheckout = !state.cart[shipper_id].isCheckout;
    },
  },
});

export default cartSlice.reducer;

export const {
  ADD_TO_CART,
  UPDATE_CART,
  SET_QUANTITY,
  PATCH_CART_ITEM,
  INCREMENT,
  DECREMENT,
  REMOVE,
  REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION,
  UPDATE_MODIFIERS,
  STOP,
  EXCLUDE,
  EXCLUDE_STOP,
  UPDATE_PRICES,
  CLEAR,
  ADD_COMMENT,
  DELETE_COMMENT,
  TOGGLE_CHECKOUT,
} = cartSlice.actions;
